import { SuperAdminLayout } from "@/components/admin/SuperAdminLayout";
import { KPICards } from "@/components/admin/KPICards";
import { RewardsSection } from "@/components/admin/RewardsSection";
import { LeaderboardSection } from "@/components/admin/LeaderboardSection";
import { useAuth } from "@/hooks/useAuth";
import { useJobOperations } from "@/hooks/useJobOperations";
import { useQuery } from "@tanstack/react-query";
import { getAdminStats, getLeaderboard, getRecruiterAdminStats } from "@/api/stats";
import { UserRole } from "@/lib/schema";

export default function SuperAdmin() {
  const { userData, currentUser } = useAuth();

  const isRecruiter = userData?.role === UserRole.RECRUITER;

  // Fetch jobs data using existing hook
  const {
    jobsData,
    loading: jobsLoading
  } = useJobOperations({ userData, currentUser });

  // Fetch admin stats - use recruiter-specific stats if recruiter
  const { data: adminStats, isLoading: statsLoading } = useQuery({
    queryKey: isRecruiter ? ["/api/stats/recruiter", currentUser?.uid] : ["/api/stats/admin"],
    queryFn: isRecruiter 
      ? () => getRecruiterAdminStats(currentUser?.uid || '') 
      : getAdminStats,
    enabled: !!currentUser,
  });

  // Fetch leaderboard data - same for all roles
  const { data: leaderboardData = [], isLoading: leaderboardLoading } = useQuery({
    queryKey: ["/api/leaderboard"],
    queryFn: () => getLeaderboard(10),
    enabled: !!currentUser,
  });

  const isLoading = jobsLoading || statsLoading || leaderboardLoading;

  // Dynamic title and subtitle based on role
  const title = isRecruiter ? "Recruiter Dashboard" : "Super Admin Dashboard";
  const subtitle = isRecruiter 
    ? "Monitor your jobs, referrals, and track placements." 
    : "Monitor KPIs, manage operations, and oversee platform performance.";

  return (
    <SuperAdminLayout 
      title={title}
      subtitle={subtitle}
    >
      {/* KPI Cards */}
      <KPICards 
        jobsData={jobsData} 
        adminStats={adminStats} 
        loading={isLoading}
        hideRewards={isRecruiter}
      />

      {/* Middle Section: Rewards Cards and Leaderboard */}
      <div className={`grid grid-cols-1 ${isRecruiter ? 'lg:grid-cols-1' : 'lg:grid-cols-3'} gap-6 mb-8`}>
        {/* Rewards Section - Hide for recruiters */}
        {!isRecruiter && (
          <RewardsSection adminStats={adminStats} loading={isLoading} />
        )}

        {/* Leaderboard Section */}
        <div className={isRecruiter ? '' : 'lg:col-span-2'}>
          <LeaderboardSection 
            loading={isLoading}
            leaderboardData={leaderboardData}
          />
        </div>
      </div>
    </SuperAdminLayout>
  );
}